import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import ActivityWizard from "../Activity/ActivityWizard";
import { useActivityDetailV2 } from "../Activity/hooks/useActivityDetailV2";
import { useEditActivityV2 } from "../Activity/hooks/useEditActivityV2";
import { useSubmitActivityV2 } from "../AddActivity/hooks/useSubmitActivityV2";
import { ActivityWizardData } from "../AddActivity/types";
import { useSaveDraft } from "../hooks/useSaveDraft";
import { PERMISSIONS, usePermissions } from "@utils/permissions";

interface EditActivityProps {
  duplication?: boolean;
}

const EditActivity: React.FC<EditActivityProps> = ({ duplication = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("wizard-activity");
  const { id: corporateId = "", idLO = "" } = useParams();
  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  const [successOpen, setSuccessOpen] = useState(false);
  const { can } = usePermissions();
  const readOnly = !duplication && !can(PERMISSIONS.catalog.write);

  const {
    wizardData,
    initialInitiativeIds,
    initialEditorial,
    isLoading,
    error,
    mutate,
  } = useActivityDetailV2({ corporateId, idLO });

  const { submit: submitEdit, isSubmitting: isSubmittingEdit } =
    useEditActivityV2({
      corporateId,
      loId: Number(idLO),
      initialInitiativeIds,
      initialEditorial,
    });

  const { submit: submitAdd, isSubmitting: isSubmittingAdd } =
    useSubmitActivityV2({ corporateId });

  const { saveDraft, isSavingDraft } = useSaveDraft({
    corporateId,
    type: "activity",
  });

  const isSubmitting = duplication ? isSubmittingAdd : isSubmittingEdit;

  const initialData = useMemo<ActivityWizardData | null>(() => {
    if (!wizardData) return null;
    if (!duplication) return wizardData;
    return {
      ...wizardData,
      title: `${wizardData.title || ""} - Copied`,
      schedule_publication: false,
      publish_date: null,
      schedule_obsolescence: false,
      obsolescent_date: null,
      useful_links: wizardData.useful_links?.map((link) => ({
        ...link,
        id: null,
      })),
    };
  }, [wizardData, duplication]);

  const handleComplete = async (data: ActivityWizardData) => {
    try {
      if (duplication) {
        await submitAdd(data);
      } else {
        await submitEdit(data);
      }
      setSuccessOpen(true);
    } catch (err) {
      console.error(
        duplication
          ? "[wizard-activity] duplicate failed"
          : "[wizard-activity] edit failed",
        err,
      );
      throw err;
    }
  };

  const handleSaveDraft = async (data: ActivityWizardData) => {
    try {
      await saveDraft(data);
      navigate(`${catalogUrl}?tab=all`);
    } catch (err) {
      console.error("[wizard-activity] saveDraft failed", err);
      throw err;
    }
  };

  const handleDismissSuccess = () => {
    if (!duplication) void mutate();
    navigate(catalogUrl);
  };

  if (isLoading || (!initialData && !error)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !initialData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {t("edit-load-error.title") || "Impossibile caricare l'attività"}
        </Typography>
        <Typography sx={{ fontSize: "13px" }}>
          {error || t("edit-load-error.description") || ""}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ActivityWizard
        initialData={initialData}
        onComplete={handleComplete}
        onSaveDraft={duplication ? handleSaveDraft : undefined}
        isSavingDraft={isSavingDraft}
        isSubmitting={isSubmitting}
        mode={duplication ? "add" : "edit"}
        readOnly={readOnly}
        duplication={duplication}
      />
      <LOOutcomeModalCst
        open={successOpen}
        entityType="activity"
        mode={duplication ? "add" : "edit"}
        onDismiss={handleDismissSuccess}
      />
    </>
  );
};

export default EditActivity;
